import React, { useState, useMemo } from 'react';
import {
  Alert,
  Button,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  OutlinedInput,
  Paper,
  Box,
} from '@mui/material';
import { VisibilityOff, Visibility } from '@mui/icons-material';
import { toast } from 'react-toastify';
import useStore from '../../states/store';
// Components
import OTPModal from './components/OTPModal';
// API
import { login, loginOTP, generateOTP } from '../../api/loginApi';
// Context
import useLoginContext from './context/useLoginContext';
import './login.css';

export default function Login() {
  const MANAGER_DESIGNATIONS = [
    'KASM',
    'KASOM',
    'KASROM',
    'KASH',
    'SAM',
    'RSM',
    'NSM-HOSPITAL',
    'NSM-SPARTAN',
    'HTAM',
    'admin',
    'SD',
    'PM',
    'DAM',
    'GPM',
    'CNC-ADMIN',
    'CNC-MANAGER',
    'CNC-ANALYST',
    'COO',
    'EXECUTIVE',
    'SFE',
  ];
  const setAuth = useStore((state) => state.setAuth);

  const [showAlert, setShowAlert] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [otpModal, setOtpModal] = useState({ toggle: false, obj: {}, otp: {} });

  const handleInputChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleLogin = () => {
    const request = {
      email: form.username,
      password: form.password,
    };
    login(request)
      .then((res) => {
        const { authenticated, data, token: accessToken } = res.data;

        if (authenticated) {
          setAuth({ ...data, token: accessToken });
        }
      })
      .catch((error) => toast.error(error));
  };

  const handleGenerateOtp = async (data = {}) => {
    const response = await generateOTP(form);
    if (response?.status === 200) {
      setOtpModal({ ...otpModal, toggle: true, obj: data, otp: response?.data });
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const response = await loginOTP(form);
      const { authenticated, data } = response.data;

      if (response?.status !== 200 || !MANAGER_DESIGNATIONS.includes(data.designation)) {
        setShowAlert('Unauthorized user.');
        setLoading(false);
        return;
      }

      if (authenticated && MANAGER_DESIGNATIONS.includes(data.designation)) {
        if (!response?.data?.is_otp_enabled) {
          handleLogin();
        }
        handleGenerateOtp(data);
        return;
      }

      setLoading(false);
    } catch (error) {
      setLoading(false);
      setShowAlert('Unauthorized user.');
    }
  };

  const handleAlertClose = () => setShowAlert(false);

  return (
    <useLoginContext.Provider
      value={useMemo(
        () => ({
          form,
          otpModal,
          setOtpModal,
        }),
        [otpModal.toggle],
      )}
    >
      <Box>
        {loading && <LinearProgress />}
        <div className='login-page'>
          <Paper className='login-container' elevation={5}>
            {showAlert && (
              <Alert severity='error' onClose={handleAlertClose} sx={{ mb: 2 }}>
                {showAlert}
              </Alert>
            )}

            <div className='login-form'>
              <div className='login-title'>BK CRM Web Portal</div>

              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel htmlFor='username' size='small'>
                  Username
                </InputLabel>
                <OutlinedInput
                  type='text'
                  id='username'
                  size='small'
                  name='username'
                  label='username'
                  value={form.username}
                  onChange={handleInputChange}
                />
              </FormControl>

              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel htmlFor='password' size='small'>
                  Password
                </InputLabel>
                <OutlinedInput
                  id='password'
                  size='small'
                  name='password'
                  value={form.password}
                  type={showPassword ? 'text' : 'password'}
                  endAdornment={
                    <InputAdornment position='end'>
                      <IconButton
                        aria-label='toggle password visibility'
                        onClick={handleClickShowPassword}
                        edge='end'
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                  label='Password'
                  onChange={handleInputChange}
                />
              </FormControl>

              <Button
                fullWidth
                variant='contained'
                onClick={(e) => handleSubmit(e)}
                disabled={loading || form.username === '' || form.password === ''}
              >
                Submit
              </Button>
            </div>
          </Paper>
        </div>
        {otpModal?.toggle ? <OTPModal /> : ''}
      </Box>
    </useLoginContext.Provider>
  );
}
